/**
 * TypeScript Color Types - IDE Autocomplete Support
 *
 * Provides type-safe color IDs with full IDE autocomplete.
 * Prevents typos and invalid color references at compile time.
 *
 * @example
 * ```typescript
 * import { getTypedColor, ColorId } from './color-types';
 *
 * // ✅ IDE suggests valid color IDs
 * const color = getTypedColor('advantage');
 *
 * // ❌ TypeScript error: 'advatage' is not a valid ColorId
 * const typo = getTypedColor('advatage');
 * ```
 */

import {colorRegistry, type ColorInfo} from './registry'

// ============================================================================
// TYPE-SAFE COLOR IDS
// ============================================================================

/**
 * Union type of all valid color IDs from the registry
 * This enables IDE autocomplete and compile-time validation
 */
export type ColorId = keyof typeof colorRegistry

/**
 * Array of all valid color IDs (for runtime validation)
 */
export const ALL_COLOR_IDS = Object.keys(colorRegistry)

/**
 * Check if a string is a valid color ID
 */
export function isValidColorId(id: string): id is ColorId {
	return id in colorRegistry
}

// ============================================================================
// TYPE-SAFE COLOR GETTERS
// ============================================================================

/**
 * Get color by ID with type safety
 * @param id - Must be a valid ColorId (IDE will autocomplete)
 * @returns ColorInfo for the color
 */
export function getTypedColor(id: ColorId): ColorInfo {
	const color = colorRegistry[id]
	if (!color) {
		throw new Error(`Color not found: ${id}`)
	}
	return color
}

/**
 * Get color by ID with runtime validation
 * @param id - Any string (validated at runtime)
 * @returns ColorInfo or undefined if not found
 */
export function getColorSafe(id: string): ColorInfo | undefined {
	if (isValidColorId(id)) {
		return colorRegistry[id]
	}
	return undefined
}

// ============================================================================
// TYPE-SAFE TOWER TYPES
// ============================================================================

/**
 * Valid tower names
 */
export type TowerName = 'pet' | 'vet' | 'farm' | 'corporate'

/**
 * Array of all tower names
 */
export const ALL_TOWERS: TowerName[] = ['pet', 'vet', 'farm', 'corporate']

/**
 * Check if a string is a valid tower name
 */
export function isValidTowerName(tower: string): tower is TowerName {
	return ALL_TOWERS.includes(tower as TowerName)
}

// ============================================================================
// TYPE-SAFE BRAND TYPES
// ============================================================================

/**
 * Extract all unique brand names from registry
 */
function extractAllBrands(): string[] {
	const brands = new Set<string>()
	Object.values(colorRegistry).forEach((color) => {
		color.brands.forEach((brand) => brands.add(brand))
	})
	return Array.from(brands).sort((a, b) => a.localeCompare(b))
}

/**
 * Array of all brand names in the registry
 */
export const ALL_BRANDS = extractAllBrands()

/**
 * Check if a string is a registered brand
 */
export function isRegisteredBrand(brand: string): boolean {
	return ALL_BRANDS.includes(brand)
}

// ============================================================================
// UTILITY TYPES
// ============================================================================

/**
 * Color with guaranteed hex code (for type narrowing)
 */
export type ColorWithHex = ColorInfo & {hexCode: string}

/**
 * Map of color ID to hex code
 */
export type ColorHexMap = Record<ColorId, string>

/**
 * Generate a map of all color IDs to their hex codes
 */
export function getColorHexMap(): ColorHexMap {
	const map = {} as ColorHexMap
	for (const id of ALL_COLOR_IDS) {
		const color = colorRegistry[id]
		if (color) {
			map[id] = color.hexCode
		}
	}
	return map
}
